import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatNumber, formatDate } from '../../utils/format';
import { canWrite } from '../../utils/roles';
import './QuotesPage.css';

const STATUS = {
  posible:    { label: 'Posible',    color: '#f59e0b' },
  confirmado: { label: 'Confirmado', color: '#22c55e' },
  cancelado:  { label: 'Cancelado',  color: '#ef4444' },
};

const ALL_STATUSES = [
  ['all', 'Todas'],
  ...Object.entries(STATUS).map(([k, v]) => [k, v.label]),
];

const COLUMNS = [
  { key: 'folio',       label: 'Código' },
  { key: 'event_date',  label: 'Fecha' },
  { key: 'title',       label: 'Nombre' },
  { key: 'client_name', label: 'Cliente' },
  { key: 'lugar_name',  label: 'Lugar' },
  { key: 'vendedor',    label: 'Vendedor' },
  { key: 'status',      label: 'Estado' },
  { key: 'net',         label: 'Monto' },
];

const norm = s => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Monto sin IVA, con el descuento global ya aplicado (subtotal = líneas con
// su descuento por ítem; luego se resta el descuento general de la cotización).
function netAmount(q) {
  const subtotal = Number(q.subtotal) || 0;
  const dv = Number(q.discount_value) || 0;
  const disc = q.discount_type === 'PERCENT' ? subtotal * dv / 100 : dv;
  return Math.max(0, subtotal - disc);
}

function formatMoney(n) {
  if (!n && n !== 0) return '—';
  return formatNumber(n);
}

export default function QuotesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = canWrite(user, 'ventas.cotizaciones');

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'event_date', dir: 'desc' });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/quotes');
      setQuotes(Array.isArray(data) ? data : []);
    } catch {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleSort = key => {
    setSort(s => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  const rows = useMemo(() => {
    let list = filterStatus === 'all' ? quotes : quotes.filter(q => q.status === filterStatus);
    const q = norm(search);
    if (q) {
      const words = q.split(/\s+/).filter(Boolean);
      list = list.filter(r => {
        const hay = norm(`${r.folio} ${r.title} ${r.client_name} ${r.client_razon} ${r.lugar_name} ${r.espacio_name} ${r.vendedor}`);
        return words.every(w => hay.includes(w));
      });
    }
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (key === 'net') return (netAmount(a) - netAmount(b)) * mul;
      const av = a[key]; const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return String(av).localeCompare(String(bv), 'es', { numeric: true }) * mul;
    });
  }, [quotes, filterStatus, search, sort]);

  const handleNew = async () => {
    setCreating(true);
    try {
      // El folio LNGNNNN se asigna en el momento de crear — atómico, sin repetidos.
      const { data } = await api.post('/quotes', {
        title: 'Nueva cotización',
        status: 'posible',
        items: [
          { section: 'General', description: 'General', quantity: 0, unit_price: 0, discount: 0, is_header: true, note: null, order: 0 },
          { section: 'General', description: '', quantity: 1, unit_price: 0, discount: 0, is_header: false, note: null, order: 1 },
        ],
      });
      navigate(`/ventas/cotizaciones/${data.id}`);
    } catch (e) {
      alert(e.response?.data?.error || 'Error al crear cotización');
      setCreating(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Cotizaciones</h1>
        {isAdmin && (
          <button className="btn btn-primary" onClick={handleNew} disabled={creating}>
            {creating ? 'Creando…' : '+ Nueva'}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="form-input"
          style={{ flex: 1, minWidth: 200, maxWidth: 340 }}
          placeholder="Buscar por código, título, cliente o lugar…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {ALL_STATUSES.map(([k, label]) => (
            <button
              key={k}
              className={`btn ${filterStatus === k ? 'btn-primary' : 'btn-ghost'}`}
              style={{ fontSize: 12.5 }}
              onClick={() => setFilterStatus(k)}
            >
              {label}
            </button>
          ))}
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>
          {rows.length} {rows.length === 1 ? 'cotización' : 'cotizaciones'}
        </span>
      </div>

      <div className="qt-table">
        <div className="qt-head">
          {COLUMNS.map(c => (
            <button key={c.key} type="button" className="qt-th" onClick={() => toggleSort(c.key)}>
              {c.label}
              {sort.key === c.key && <span className="qt-sort">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
            </button>
          ))}
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center' }}><div className="spinner" /></div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-dim)' }}>
            {search || filterStatus !== 'all' ? 'Sin resultados.' : 'No hay cotizaciones todavía.'}
          </div>
        ) : (
          rows.map(q => {
            const s = STATUS[q.status] || STATUS.posible;
            return (
              <div key={q.id} className="qt-row" onClick={() => navigate(`/ventas/cotizaciones/${q.id}`)}>
                <span className="qt-folio">{q.folio || '—'}</span>
                <span className="qt-cell">{formatDate(q.event_date) || '—'}</span>
                <span className="qt-title">{q.title}</span>
                <span className="qt-cell">{q.client_name || '—'}</span>
                <span className="qt-cell">{q.lugar_name ? `${q.lugar_name}${q.espacio_name ? ` - ${q.espacio_name}` : ''}` : '—'}</span>
                <span className="qt-cell">{q.vendedor || '—'}</span>
                <span className="qt-status" style={{ background: `${s.color}22`, color: s.color }}>{s.label}</span>
                <span className="qt-total">${formatMoney(netAmount(q))}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
